import { useEffect, useState } from "react";
import {
  ReportFilterBody,
  ReportFilterContainer,
  ReportHorizontalSeparator,
  ReportSectionHeading,
  MultiSelectButton,
  TransactionGrid,
} from "../ReportFilterPageStyles";
import { H3Heading } from "../../appSettings/AppSettingsStyles";
import { useNavigate } from "react-router-dom";
import { CommonModal, CustomButton, CustomDateField, CustomSelectField, Loader, NavigateBack } from "../../../components";
import { useAppSelector } from "../../../_app";
import { Notify, Utils } from "../../../utils";
import { EmployeeService } from "../../../_service";
import { LoaderContainer } from "../../../CommonStyles";
import moment from "moment";
import ExcelJS from "exceljs";
import EmployeeListModal from "../EmployeeListModal/EmployeeListModal";
import styled from "styled-components";

const ALL_COLUMNS = [
  "Employee Name",
  "Hourly",
  "Date Joined",
  "Resigned Date",
  "Employee ID",
  "Basic Salary",
  "Bonus Amount",
  "EE CPF",
  "Co CPF",
  "Total CPF",
  "SDL",
  "Bank to Pay",
  "To Pay CPF",
  "No. of Bonus Months",
];

const STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Draft", value: "preview" },
  { label: "Confirmed", value: "finalized" },
  { label: "Executed", value: "executed" },
  { Label: "Sent to TellBoss", value: "sent_for_approval" },
];

const AnnualBonusReport = () => {
  const navigate = useNavigate();
  const companyId = useAppSelector((state) => state.user.defaultCompnayId);
  const companyName = useAppSelector((state) => state.user.defaultCompany);

  const [isLoading, setIsLoading] = useState(false);
  const [listingData, setListingData] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [selectedBonusIds, setSelectedBonusIds] = useState<any[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([...ALL_COLUMNS]);
  const [isExporting, setIsExporting] = useState(false);
  const [fromDate, setFromDate] = useState<string>(moment().startOf("year").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [modalBonusRecords, setModalBonusRecords] = useState<any[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<any[]>([]);
  const [activeBonusId, setActiveBonusId] = useState<any>(null);
  const [excludedEmployees, setExcludedEmployees] = useState<Record<string, any[]>>({});

  const yearOptions = ["all", ...Utils.getDefaultYearRange()];

  const fetchListingData = async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const res = await EmployeeService._getAnnualBonusListing(companyId);
      if (res.status >= 200 && res.status < 300) {
        setListingData(res?.data?.data?.bonuses || []);
      }
    } catch (e: any) {
      Notify("Something went wrong", 0);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchListingData();
  }, [companyId]);

  const filteredData = listingData.filter((item: any) => {
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    const matchesYear = yearFilter === "all" || !item.period_to || moment(item.period_to).format("YYYY") === yearFilter;
    // const matchesDateRange = !item.period_to || (moment(item.period_to).isSameOrAfter(fromDate) && moment(item.period_to).isSameOrBefore(toDate));
    return matchesStatus && matchesYear;
  });

  const toggleBonusSelection = (id: any) => {
    setSelectedBonusIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllBonuses = () => {
    if (selectedBonusIds.length === filteredData.length) {
      setSelectedBonusIds([]);
    } else {
      setSelectedBonusIds(filteredData.map((item: any) => item.id));
    }
  };

  const toggleColumn = (col: string) => {
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((x) => x !== col) : [...prev, col]
    );
  };

  const selectAllColumns = () => setSelectedColumns([...ALL_COLUMNS]);
  const selectNoneColumns = () => setSelectedColumns([]);

  const handleRowClick = async (bonusId: any) => {
    return;
    setActiveBonusId(bonusId);
    try {
      const res = await EmployeeService._getEmployeeAnnualBonusEditData(bonusId);
      if (res.status === 200) {
        const records = res?.data?.data?.bonus_records || [];
        setModalBonusRecords(records);
        const excluded = excludedEmployees[bonusId] || [];
        setSelectedEmployeeIds(records.filter((r: any) => !excluded.includes(r.employee)).map((r: any) => r.employee));
        setShowEmployeeModal(true);
      }
    } catch (e: any) {
      Notify("Something went wrong", 0);
    }
  };

  const handleSelectAllEmployees = () => {
    setSelectedEmployeeIds(modalBonusRecords.map((r: any) => r.employee));
  };

  const handleSelectNoneEmployees = () => {
    setSelectedEmployeeIds([]);
  };

  const handleToggleEmployee = (empId: any) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(empId) ? prev.filter((x) => x !== empId) : [...prev, empId]
    );
  };

  const handleCloseModal = () => {
    if (activeBonusId) {
      const allEmpIds = modalBonusRecords.map((r: any) => r.employee);
      const excluded = allEmpIds.filter((id: any) => !selectedEmployeeIds.includes(id));
      setExcludedEmployees((prev) => ({ ...prev, [activeBonusId]: excluded }));
    }
    setShowEmployeeModal(false);
    setActiveBonusId(null);
  };

  const downloadExcel = async () => {
    if (selectedBonusIds.length === 0 || selectedColumns.length === 0) return;
    setIsExporting(true);

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Annual Bonus Report");

      worksheet.addRow([companyName]);
      worksheet.addRow(["Annual Bonus Report"]);
      worksheet.addRow([]);

      const headerRows = new Set([1, 2, 3]);
      let currentRow = 4;

      for (const bonusId of selectedBonusIds) {
        const bonus = listingData.find((b: any) => b.id === bonusId);
        if (!bonus) continue;

        const res = await EmployeeService._getEmployeeAnnualBonusEditData(bonusId);
        if (res.status !== 200) continue;

        const bonusRecords = res?.data?.data?.bonus_records || [];
        const excluded = excludedEmployees[bonusId] || [];
        const filteredRecords = bonusRecords.filter((emp: any) => !excluded.includes(emp.employee));

        const statusLabel = bonus.status === "preview" ? "Draft" : bonus.status === "finalized" ? "Confirmed" : bonus.status === "executed" ? "Executed" : bonus.status === "sent_for_approval" ? "Sent to TellBoss" : bonus.status || "-";
        worksheet.addRow(["Bonus Year", moment(bonus.period_to).format("DD-MM-YYYY")]);
        worksheet.getRow(currentRow).font = { bold: true, size: 11 };
        headerRows.add(currentRow);
        currentRow++;

        worksheet.addRow(["Payrun Month", bonus.effective_date ? moment(bonus.effective_date).format("DD-MM-YYYY") : "-"]);
        worksheet.getRow(currentRow).font = { bold: true, size: 11 };
        headerRows.add(currentRow);
        currentRow++;

        worksheet.addRow(["Status", statusLabel]);
        worksheet.getRow(currentRow).font = { bold: true, size: 11 };
        headerRows.add(currentRow);
        currentRow++;

        worksheet.addRow(["Number of Employees", filteredRecords.length]);
        worksheet.getRow(currentRow).font = { bold: true, size: 11 };
        headerRows.add(currentRow);
        currentRow++;

        worksheet.addRow([]);
        currentRow++;

        worksheet.addRow(selectedColumns);
        worksheet.getRow(currentRow).font = { bold: true };
        headerRows.add(currentRow);
        currentRow++;

        let totalEmployee = 0;
        let totalSalary = 0;
        let totalBonusAmount = 0;
        let totalCpfEmployee = 0;
        let totalCpfEmployer = 0;
        let totalCpfTotal = 0;
        let totalSdl = 0;

        for (const emp of filteredRecords) {
          const row = selectedColumns.map((col) => {
            switch (col) {
              case "Employee Name":
                return emp.employee_name || "";
              case "Hourly":
                return emp.salary_type || "";
              case "Date Joined":
                return emp.date_joined ? moment(emp.date_joined).format("DD-MM-YYYY") : "";
              case "Resigned Date":
                return emp.resigned_date ? moment(emp.resigned_date).format("DD-MM-YYYY") : "";
              case "Employee ID":
                return emp.employee || "";
              case "Basic Salary":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  `${emp.basic_salary}`?.replace(/,/g, "")
                );
              case "Bonus Amount":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  `${emp.bonus_amount}`.replace(/,/g, "")
                );
              case "EE CPF":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  (parseFloat(emp.cpf_employee || 0)).toFixed(2)
                );
              case "Co CPF":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  (parseFloat(emp.cpf_employer || 0)).toFixed(2)
                );
              case "Total CPF":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  (parseFloat(emp.cpf_total || 0)).toFixed(2)
                );
              case "SDL":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  (parseFloat(emp.sdl || 0)).toFixed(2)
                );
              case "Bank to Pay":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  ((parseFloat(emp.bonus_amount) || 0) - (parseFloat(emp.cpf_employee) || 0)).toFixed(2)
                );
              case "To Pay CPF":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  ((parseFloat(emp.cpf_total) || 0) + (parseFloat(emp.sdl) || 0)).toFixed(2)
                );
              case "No. of Bonus Months":
                return (parseFloat(emp.no_of_bonus_months || 0)).toFixed(2);
              default:
                return "";
            }
          });
          worksheet.addRow(row);
          currentRow++;

          totalEmployee++;
          totalSalary += parseFloat(`${emp.basic_salary}`?.replace(/,/g, "")) || 0;
          totalBonusAmount += parseFloat(emp.bonus_amount) || 0;
          totalCpfEmployee += parseFloat(emp.cpf_employee) || 0;
          totalCpfEmployer += parseFloat(emp.cpf_employer) || 0;
          totalCpfTotal += parseFloat(emp.cpf_total) || 0;
          totalSdl += parseFloat(emp.sdl) || 0;
        }

        worksheet.addRow([]);
        currentRow++;

        const totalsRow = selectedColumns.map((col) => {
          switch (col) {
            case "Employee Name":
              return "TOTAL";
            case "Basic Salary":
              return Utils.getCommanSepartedNumericStringWithDollar(totalSalary.toFixed(2));
            case "Bonus Amount":
              return Utils.getCommanSepartedNumericStringWithDollar(totalBonusAmount.toFixed(2));
            case "EE CPF":
              return Utils.getCommanSepartedNumericStringWithDollar(totalCpfEmployee.toFixed(2));
            case "Co CPF":
              return Utils.getCommanSepartedNumericStringWithDollar(totalCpfEmployer.toFixed(2));
            case "Total CPF":
              return Utils.getCommanSepartedNumericStringWithDollar(totalCpfTotal.toFixed(2));
            case "SDL":
              return Utils.getCommanSepartedNumericStringWithDollar(totalSdl.toFixed(2));
            case "Bank to Pay":
              return Utils.getCommanSepartedNumericStringWithDollar((totalBonusAmount - totalCpfEmployee).toFixed(2));
            case "To Pay CPF":
              return Utils.getCommanSepartedNumericStringWithDollar((totalCpfTotal + totalSdl).toFixed(2));
            default:
              return "";
          }
        });
        worksheet.addRow(totalsRow);
        worksheet.getRow(currentRow).font = { bold: true };
        currentRow++;

        worksheet.addRow([]);
        currentRow++;

        worksheet.addRow([]);
        currentRow++;
      }

      worksheet.getRow(1).font = { bold: true, size: 14 };
      worksheet.getRow(2).font = { bold: true };

      worksheet.columns.forEach((column, index) => {
        let maxLength = 0;
        if (column && typeof column.eachCell === "function") {
          column.eachCell({ includeEmpty: true }, (cell) => {
            const length = cell.value ? cell.value.toString().length : 0;
            if (length > maxLength) {
              maxLength = length;
            }
          });
        }
        column.width = Math.max(maxLength + 4, 18);
      });

      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell, colNumber) => {
          const colName = selectedColumns[colNumber - 1];
          if (colName && ["Date Joined", "Employee ID", "Resigned Date", "Hourly", "Basic Salary", "Bonus Amount", "EE CPF", "Co CPF", "Total CPF", "SDL", "Bank to Pay", "To Pay CPF", "No. of Bonus Months"].includes(colName)) {
            cell.alignment = { horizontal: "right" };
          } else {
            cell.alignment = { horizontal: "left" };
          }
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${companyName}_Annual_Bonus_Report.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      Notify("Something went wrong while exporting", 0);
    }
    setIsExporting(false);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "preview":
        return <p style={{ color: "#000", fontWeight: "bold" }}>Draft</p>;
      case "finalized":
        return <p style={{ color: "rgb(232, 92, 13)", fontWeight: "bold" }}>Confirmed</p>;
      case "executed":
        return <p style={{ color: "rgb(30, 81, 40)", fontWeight: "bold" }}>Executed</p>;
      case "sent_for_approval":
        return <p style={{ color: "rgb(30, 81, 40)", fontWeight: "bold" }}>Sent to TellBoss</p>;
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: ".5rem",
        }}
      >
        <NavigateBack />
        <H3Heading>{Utils.getTitleWithCompanyName("Annual Bonus Report")}</H3Heading>
      </div>

      <ReportFilterContainer>
        <ReportFilterBody>
          <div>
            <ReportSectionHeading>Filters</ReportSectionHeading>
            <div style={{ display: "flex", gap: 50, marginTop: 20 }}>
              <div style={{ display: "flex" }}>
                <Container>
                  <Text>Bonus Status</Text>
                  <Select
                    value={statusFilter}
                    onChange={(e: any) => {
                      setStatusFilter(e.target.value);
                      setSelectedBonusIds([]);
                    }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label || opt.Label}
                      </option>
                    ))}
                  </Select>
                </Container>
              </div>
              <div style={{ display: "flex" }}>
                <Container>
                  <Text>Bonus Year</Text>
                  <Select
                    value={yearFilter}
                    onChange={(e: any) => {
                      setYearFilter(e.target.value);
                      setSelectedBonusIds([]);
                    }}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year === "all" ? "All" : year}
                      </option>
                    ))}
                  </Select>
                </Container>
              </div>
            </div>
          </div>

          {/* <ReportHorizontalSeparator style={{ marginTop: 6 }} />

          <div>
            <ReportSectionHeading>Date Range</ReportSectionHeading>
            <div style={{ display: "flex", width: "30%", alignItems: "center", gap: "10px", marginTop: 10 }}>
              <CustomDateField
                label="From"
                date={fromDate}
                setDate={(date: any) => setFromDate(date)}
                labelWidth="40px"
              />
              <CustomDateField
                label="To"
                date={toDate}
                setDate={(date: any) => setToDate(date)}
                labelWidth="20px"
              />
            </div>
          </div>

          <ReportHorizontalSeparator style={{ marginTop: 6 }} /> */}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <ReportSectionHeading>
              Bonus Records ({selectedBonusIds.length} selected)
            </ReportSectionHeading>
            <CustomButton
              width="200px"
              title={isExporting ? "Exporting..." : "Show Excel"}
              onClick={downloadExcel}
              disable={selectedBonusIds.length === 0 || selectedColumns.length === 0 || isExporting}
            />
          </div>

          <div className="table" style={{ marginTop: "1rem" }}>
            <table className="company-table">
              <thead className="company-table-head">
                <tr>
                  <th style={{ textAlign: "center", width: "4%" }}>
                    <input
                      type="checkbox"
                      checked={selectedBonusIds.length === filteredData.length && filteredData.length > 0}
                      onChange={toggleSelectAllBonuses}
                    />
                  </th>
                  <th style={{ textAlign: "left", width: "13%" }}>Bonus Year</th>
                  <th style={{ textAlign: "center", width: "10%" }}>Payrun Month</th>
                  <th style={{ textAlign: "right", width: "13%" }}>Total Salary</th>
                  <th style={{ textAlign: "right", width: "12%" }}>Total Bonus</th>
                  <th style={{ textAlign: "right", width: "14%" }}>Average Bonus Month</th>
                  <th style={{ textAlign: "right", width: "16%" }}>Total CPF on Total Bonus</th>
                  <th style={{ textAlign: "center", width: "12%" }}>Status</th>
                  <th style={{ textAlign: "center", width: "20%" }}>Date Created</th>
                  <th style={{ textAlign: "left", width: "15%" }}>Action</th>
                </tr>
              </thead>
              <thead>
                {filteredData?.length > 0 &&
                  filteredData.map((item: any) => (
                    <tr key={item.id} onClick={() => handleRowClick(item.id)} style={{ cursor: "default" }}>
                      <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedBonusIds.includes(item.id)}
                          onChange={() => toggleBonusSelection(item.id)}
                          style={{ width: 14, height: 14 }}
                        />
                      </td>
                      <td>{moment(item?.period_to).format("DD-MM-YYYY")}</td>
                      <td style={{ textAlign: "center" }}>
                        {item?.effective_date == null
                          ? "-"
                          : moment(item?.effective_date).format("DD-MM-YYYY")}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {"$" + Utils.getNumberWithCommas(item?.total_salary?.toFixed(2))}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {"$" + Utils.getNumberWithCommas(item?.total_bonus_amount?.toFixed(2))}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {item?.avg_bonus_month?.toFixed(2) || ""}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {Utils.getCommanSepartedNumericStringWithDollar(
                          item?.total_cpf_total?.toFixed(2)
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>{getStatusLabel(item?.status)}</td>
                      <td style={{ textAlign: "center" }}>
                        {item?.created_at
                          ? moment(item?.created_at).format("DD-MM-YYYY")
                          : "-"}
                      </td>
                      <td style={{ textAlign: "right", width: "30%" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "left",
                            justifyContent: "left",
                            gap: "1rem",
                          }}
                        >
                          <p
                            style={{
                              padding: "0.25rem 0.75rem",
                              background: "#03B381",
                              cursor: "pointer",
                              color: "white",
                              fontWeight: 400,
                              borderRadius: "0.25rem",
                            }}
                            onClick={() => {
                              navigate(
                                `/dashboard/annual-bonus/view`,
                                {
                                  state: {
                                    isEdit: false,
                                    id: item?.id,
                                    isFinalized: true,
                                  },
                                }
                              );
                            }}
                          >
                            View
                          </p>
                        </div>
                      </td>
                    </tr>
                  ))}
              </thead>
            </table>
          </div>

          <ReportHorizontalSeparator style={{ marginTop: 20 }} />

          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <ReportSectionHeading>Columns to Export</ReportSectionHeading>
            <MultiSelectButton onClick={selectAllColumns}>All</MultiSelectButton>
            <MultiSelectButton onClick={selectNoneColumns}>None</MultiSelectButton>
          </div>

          <TransactionGrid style={{ marginTop: 12 }}>
            {ALL_COLUMNS.map((col) => (
              <div
                key={col}
                style={{
                  display: "flex",
                  gap: 20,
                  color: "#333333",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(col)}
                  onChange={() => toggleColumn(col)}
                />
                <p>{col}</p>
              </div>
            ))}
          </TransactionGrid>
        </ReportFilterBody>
      </ReportFilterContainer>

      <CommonModal
        handleClose={handleCloseModal}
        open={showEmployeeModal}
        updateData={handleCloseModal}
        body={
          <EmployeeListModal
            employeeList={modalBonusRecords.map((r: any) => ({ id: r.employee, name: r.employee_name || r.employee }))}
            handleSelectAll={handleSelectAllEmployees}
            handleSelectNone={handleSelectNoneEmployees}
            selectedEmployeeList={selectedEmployeeIds}
            handleSelectEmployee={handleToggleEmployee}
          />
        }
        heading="Filter Employees"
        conformButtonText="Close"
        isCancelButton={false}
      />
    </>
  );
};

export default AnnualBonusReport;

const Container = styled.div`
  margin-bottom: 1rem;
  display: flex;
`;
const Text = styled.p`
  width: 110px;
  height: 30px;
  color: #fff;
  border: 1px solid #afb5ba;
  font-size: 0.8rem;
  background-color: #afb5ba;
  text-transform: none;
  display: flex;
  align-items: center;
  padding: 0 5px;
  border-radius: 3px 0 0 3px;
`;
const Select = styled.select`
  max-width: 180px;
  height: 30px;
  background-color: #087ca7;
  color: #fff;
  border-radius: 0 3px 3px 0;
  cursor: pointer;
  padding: 0 5px;
  text-align: center;
`;
